// ── Category definitions ──────────────────────────────────────────────────
export const categories = [
  { id: "core-ml", label: "Core ML", color: "#f0c040" },
  { id: "neural-networks", label: "Neural Networks", color: "#8b5cf6" },
  { id: "transformers", label: "Transformers & LLMs", color: "#67e8f9" },
  { id: "generative", label: "Generative AI", color: "#f472b6" },
  { id: "rag", label: "RAG & Knowledge", color: "#34d399" },
  { id: "agents", label: "Agents", color: "#fb923c" },
  { id: "evaluation", label: "Evaluation", color: "#a3e635" },
  { id: "cutting-edge", label: "Cutting Edge", color: "#e879f9" },
  { id: "models", label: "Frontier Models", color: "#f87171" },
  { id: "labs", label: "AI Labs & Tools", color: "#4ade80" },
];

// Helper to attach category metadata to each term
function withCategory(terms, categoryId) {
  const cat = categories.find((c) => c.id === categoryId);
  return terms.map((t) => ({
    ...t,
    category: categoryId,
    categoryLabel: cat?.label || categoryId,
    categoryColor: cat?.color || "#888",
  }));
}

// ── Vocabulary Terms ──────────────────────────────────────────────────────
export const vocabularyTerms = [
  // ── Core ML ────────────────────────────────────────────────
  ...withCategory(
    [
      {
        id: "gradient-descent",
        term: "Gradient Descent",
        shortDef:
          "Optimization algorithm that iteratively moves toward the minimum of a loss function.",
        definition:
          "Gradient descent updates model parameters in the direction opposite to the gradient of the loss function with respect to those parameters. Each step nudges weights to reduce prediction error. Variants include batch, stochastic (SGD), and mini-batch gradient descent, which differ in how many examples they use per update. Adaptive optimizers like Adam and RMSProp build on this idea.",
        related: [
          "Backpropagation",
          "Learning Rate",
          "Loss Function",
          "Adam Optimizer",
        ],
      },
      {
        id: "backpropagation",
        term: "Backpropagation",
        shortDef:
          "Algorithm for computing gradients through a neural network using the chain rule.",
        definition:
          "Backpropagation propagates the gradient of the loss from the output layer back through each layer to update weights. It applies the chain rule of calculus to efficiently compute partial derivatives at each layer. Without it, training deep networks would be computationally intractable. It is universally used in conjunction with gradient descent.",
        related: [
          "Gradient Descent",
          "Chain Rule",
          "Loss Function",
          "Automatic Differentiation",
        ],
      },
      {
        id: "overfitting",
        term: "Overfitting",
        shortDef:
          "When a model learns the training data too well, including its noise, and fails to generalize.",
        definition:
          "An overfitting model memorizes training examples and their idiosyncrasies instead of learning the underlying pattern. It achieves high training accuracy but poor performance on held-out test data. Common remedies include regularization (L1/L2), dropout, early stopping, and data augmentation. The bias-variance tradeoff frames overfitting as high-variance behavior.",
        related: [
          "Regularization",
          "Bias-Variance Tradeoff",
          "Dropout",
          "Cross-Validation",
        ],
      },
      {
        id: "regularization",
        term: "Regularization",
        shortDef:
          "Techniques that reduce overfitting by adding constraints or penalties on model complexity.",
        definition:
          "L1 regularization (Lasso) adds the absolute value of weights to the loss, promoting sparsity. L2 (Ridge) adds the squared magnitude, shrinking weights toward zero. Dropout randomly deactivates neurons during training. Data augmentation artificially expands training sets. All these reduce the model's ability to overfit while preserving its capacity to generalize.",
        related: ["Overfitting", "L1/L2 Penalty", "Dropout", "Weight Decay"],
      },
      {
        id: "cross-validation",
        term: "Cross-Validation",
        shortDef:
          "Technique to estimate model generalization by splitting data into multiple train/test folds.",
        definition:
          "k-fold cross-validation divides the dataset into k equally-sized folds, trains on k-1, and tests on the remaining fold, repeating k times. The average performance gives a more reliable estimate than a single train/test split. Leave-one-out (LOO) is the extreme case where k equals the number of samples. It is essential for hyperparameter selection when labeled data is scarce.",
        related: ["Overfitting", "Hyperparameter Tuning", "Train/Test Split"],
      },
      {
        id: "bias-variance",
        term: "Bias-Variance Tradeoff",
        shortDef:
          "The tension between underfitting (high bias) and overfitting (high variance) in models.",
        definition:
          "Bias is the error from wrong assumptions in the learning algorithm — a too-simple model underfits. Variance is the error from sensitivity to fluctuations in the training set — a too-complex model overfits. The total generalization error decomposes into bias², variance, and irreducible noise. Finding the sweet spot between them is a core challenge in machine learning. Modern large models challenge this tradeoff with double-descent phenomena.",
        related: ["Overfitting", "Regularization", "Model Complexity"],
      },
      {
        id: "transfer-learning",
        term: "Transfer Learning",
        shortDef:
          "Reusing a model trained on one task as the starting point for a different, related task.",
        definition:
          "Instead of training from scratch, transfer learning starts with weights pretrained on a large dataset, then fine-tunes on a smaller task-specific dataset. The pretrained model has already learned general features (edges, textures, grammar) that are reusable. It dramatically reduces the amount of labeled data and compute needed. It is the foundation of modern NLP (BERT, GPT) and computer vision (ImageNet pretrained models).",
        related: [
          "Fine-tuning",
          "Pretrained Models",
          "Domain Adaptation",
          "Few-shot Learning",
        ],
      },
      {
        id: "hyperparameter",
        term: "Hyperparameter",
        shortDef:
          "Configuration settings set before training that control the learning process itself.",
        definition:
          "Unlike model parameters (weights) which are learned from data, hyperparameters are set manually or via search. Common examples include learning rate, batch size, number of layers, number of attention heads, and regularization strength. Hyperparameter tuning methods include grid search, random search, and Bayesian optimization. Poor choices can prevent convergence or cause overfitting regardless of model architecture.",
        related: [
          "Gradient Descent",
          "Bayesian Optimization",
          "Neural Architecture Search",
        ],
      },
    ],
    "core-ml",
  ),

  // ── Neural Networks ─────────────────────────────────────────
  ...withCategory(
    [
      {
        id: "perceptron",
        term: "Perceptron",
        shortDef:
          "The simplest neural unit: computes a weighted sum of inputs and applies a step function.",
        definition:
          "Introduced by Frank Rosenblatt in 1958, the perceptron takes a vector of inputs, multiplies each by a learned weight, sums them, and passes the result through a step function to produce a binary output. A single perceptron can only learn linearly separable functions. Stacking perceptrons into layers creates a multi-layer perceptron (MLP), the building block of all deep learning.",
        related: ["Activation Function", "MLP", "Neural Network"],
      },
      {
        id: "activation-function",
        term: "Activation Function",
        shortDef:
          "Nonlinear function applied after the weighted sum in a neuron, enabling complex pattern learning.",
        definition:
          "Without activation functions, stacking linear layers is mathematically equivalent to a single linear layer. ReLU (max(0,x)) is the most common — fast and avoids the vanishing gradient problem. Sigmoid and tanh are used in output layers and LSTMs. GeLU and SwiGLU are modern variants used in Transformers. The choice of activation significantly affects training dynamics.",
        related: [
          "ReLU",
          "Sigmoid",
          "Vanishing Gradient",
          "Transformer Architecture",
        ],
      },
      {
        id: "batch-normalization",
        term: "Batch Normalization",
        shortDef:
          "Normalizes layer inputs across the batch dimension to stabilize and accelerate training.",
        definition:
          "Batch normalization (BatchNorm) standardizes the inputs to a layer by subtracting the batch mean and dividing by batch standard deviation, then applying learned scale and shift parameters. It reduces internal covariate shift, allows higher learning rates, and acts as a mild regularizer. Layer Normalization (LayerNorm), which normalizes across the feature dimension, is preferred in Transformers because it works correctly at any batch size including 1.",
        related: [
          "Layer Normalization",
          "Covariate Shift",
          "Transformer Architecture",
        ],
      },
      {
        id: "dropout",
        term: "Dropout",
        shortDef:
          "Randomly deactivates a fraction of neurons during training to prevent co-adaptation.",
        definition:
          "During each training forward pass, dropout sets each neuron's output to zero with probability p (typically 0.1–0.5). This forces the network to develop multiple independent internal representations since it cannot rely on any one neuron. At inference, all neurons are active but their outputs are scaled by (1-p). Dropout is one of the simplest and most effective regularization techniques for neural networks.",
        related: ["Regularization", "Overfitting", "Ensemble Methods"],
      },
      {
        id: "cnn",
        term: "Convolutional Neural Network (CNN)",
        shortDef:
          "Neural network using learned convolutional filters to extract local spatial features from images.",
        definition:
          "CNNs replace dense matrix multiplications with convolution operations that slide small filters across the input. This gives translation invariance and dramatically reduces the number of parameters compared to fully connected layers. Pooling layers downsample spatial dimensions while retaining dominant features. Architectures like AlexNet, VGG, ResNet, and EfficientNet have driven progress in image classification, detection, and segmentation.",
        related: ["Pooling", "ResNet", "MaxPooling", "Object Detection"],
      },
      {
        id: "rnn",
        term: "Recurrent Neural Network (RNN)",
        shortDef:
          "Network with hidden state that processes sequences step-by-step, carrying memory.",
        definition:
          "RNNs process inputs one timestep at a time and maintain a hidden state that summarizes all previous context. This makes them suitable for sequences of variable length. However, they suffer from the vanishing gradient problem — gradients shrink exponentially over long sequences, making it hard to learn long-range dependencies. LSTMs and GRUs were designed to solve this. Transformers have largely replaced RNNs for most sequence tasks.",
        related: ["LSTM", "GRU", "Vanishing Gradient", "Sequence Modeling"],
      },
      {
        id: "lstm",
        term: "Long Short-Term Memory (LSTM)",
        shortDef:
          "RNN variant with gating mechanisms that can learn long-range dependencies in sequences.",
        definition:
          "LSTMs introduce three gates — input, forget, and output — plus a cell state that acts as an information highway. The forget gate decides what to discard from the cell state; the input gate decides what new information to add; the output gate controls what to expose as the hidden state. This architecture largely solves the vanishing gradient problem. LSTMs dominated sequential tasks in NLP, speech, and time-series until Transformers arrived.",
        related: [
          "RNN",
          "GRU",
          "Vanishing Gradient",
          "Transformer Architecture",
        ],
      },
      {
        id: "resnet",
        term: "Residual Network (ResNet)",
        shortDef:
          "Deep CNN with skip connections that add the input of a block directly to its output.",
        definition:
          "Introduced by He et al. in 2015, ResNet showed that very deep networks (100+ layers) can be trained effectively using residual (skip) connections. Each residual block computes F(x) + x, where x is the direct shortcut. If the block learns nothing useful, it can output zero and just pass x through. This trivial identity mapping makes optimization much easier for deep networks. ResNets won ImageNet 2015 and launched the era of extremely deep networks.",
        related: [
          "Skip Connections",
          "CNN",
          "Deep Learning",
          "Batch Normalization",
        ],
      },
      {
        id: "attention-mechanism",
        term: "Attention Mechanism",
        shortDef:
          "Allows a model to focus on relevant parts of the input when producing each output.",
        definition:
          "Attention computes a weighted sum of value vectors, where weights are determined by the similarity between query and key vectors. It was first introduced for neural machine translation (Bahdanau, 2015) as an add-on to RNN encoder-decoders. The key insight is that rather than compressing a whole sequence to one vector, attention can directly access any part of the input. This became the foundation of the Transformer architecture.",
        related: [
          "Self-Attention",
          "Transformer Architecture",
          "Multi-Head Attention",
          "Query/Key/Value",
        ],
      },
    ],
    "neural-networks",
  ),

  // ── Transformers & LLMs ─────────────────────────────────────
  ...withCategory(
    [
      {
        id: "transformer",
        term: "Transformer Architecture",
        shortDef:
          "Attention-based architecture that processes entire sequences in parallel, replacing RNNs.",
        definition:
          '"Attention Is All You Need" (Vaswani et al., 2017) introduced the Transformer, which uses only self-attention and feed-forward layers — no recurrence or convolution. The encoder processes the input sequence in parallel; the decoder auto-regressively generates output. Positional encodings inject sequence order since there is no inherent position. Transformers scale extremely efficiently with data and compute, leading to the LLM revolution.',
        related: [
          "Self-Attention",
          "Positional Encoding",
          "BERT",
          "GPT",
          "Multi-Head Attention",
        ],
      },
      {
        id: "self-attention",
        term: "Self-Attention",
        shortDef:
          "Each token attends to every other token in the same sequence to build contextualized representations.",
        definition:
          "In self-attention, each input token generates a query, key, and value vector via learned linear projections. The attention weight for a token pair is computed as the softmax of their query-key dot product, scaled by √d_k. The output for each token is a weighted sum of all value vectors. This allows every token to draw directly on context from any position in the sequence, regardless of distance — a key advantage over RNNs and CNNs.",
        related: [
          "Attention Mechanism",
          "Transformer Architecture",
          "Multi-Head Attention",
        ],
      },
      {
        id: "multi-head-attention",
        term: "Multi-Head Attention",
        shortDef:
          "Running multiple self-attention operations in parallel, each learning different relationship patterns.",
        definition:
          "Multi-head attention projects queries, keys, and values into h different subspaces, performs self-attention in each subspace independently, then concatenates and linearly projects the results. Different heads can learn syntactic, semantic, and positional relationships simultaneously. GPT-3 uses 96 heads; BERT-base uses 12. The compute cost scales as O(n²d) in sequence length n, which is a key bottleneck for very long contexts.",
        related: [
          "Self-Attention",
          "Transformer Architecture",
          "Attention Head",
        ],
      },
      {
        id: "bert",
        term: "BERT",
        shortDef:
          "Bidirectional Transformer encoder pretrained by masking tokens and predicting them from both sides.",
        definition:
          "BERT (Bidirectional Encoder Representations from Transformers, Devlin et al., 2018) is pretrained with Masked Language Modeling (MLM) — 15% of tokens are masked, and the model predicts them using full bidirectional context. Unlike GPT which reads left-to-right, BERT sees the whole sentence at once. This makes BERT strong for classification, NER, and question answering, but unsuitable for text generation. Fine-tuned BERT variants (RoBERTa, DeBERTa) still power many NLP systems.",
        related: [
          "Transformer Architecture",
          "Masked Language Model",
          "Fine-tuning",
          "GPT",
        ],
      },
      {
        id: "gpt",
        term: "GPT (Generative Pre-trained Transformer)",
        shortDef:
          "Autoregressive Transformer decoder trained to predict the next token in a sequence.",
        definition:
          "GPT (OpenAI, 2018) uses a decoder-only Transformer, trained on the causal language modeling objective — predict the next token given all previous tokens. This makes it inherently generative. GPT-3 (175B parameters) demonstrated remarkable few-shot capabilities. GPT-4 added multimodality. The GPT line inspired the modern LLM ecosystem (LLaMA, Mistral, Gemma). Scaling laws (Kaplan 2020) showed that loss decreases predictably with more parameters and data.",
        related: [
          "BERT",
          "Transformer Architecture",
          "Autoregressive",
          "Scaling Laws",
          "LLM",
        ],
      },
      {
        id: "llm",
        term: "Large Language Model (LLM)",
        shortDef:
          "Transformer-based model trained on massive text corpora, exhibiting emergent language capabilities.",
        definition:
          "LLMs are autoregressive or masked Transformers with billions of parameters, trained on web-scale text. Emergent abilities — reasoning, code generation, arithmetic — appear only above certain scale thresholds, not in smaller versions. Modern LLMs like GPT-4, Claude, Gemini, and LLaMA 3 are instruction-tuned via RLHF or DPO after pretraining. They are the backbone of chatbots, copilots, and agent systems.",
        related: [
          "GPT",
          "RLHF",
          "Emergent Abilities",
          "Scaling Laws",
          "Instruction Tuning",
        ],
      },
      {
        id: "tokenization",
        term: "Tokenization",
        shortDef:
          "Converting raw text into a sequence of discrete tokens that a model can process.",
        definition:
          "Modern tokenizers use subword algorithms: BPE (Byte Pair Encoding) or WordPiece. BPE starts with characters and iteratively merges the most frequent pair into a new token until the vocabulary size is reached. This balances coverage (rare words get split into known subwords) and efficiency (common words become single tokens). GPT-4 uses ~100k token vocabulary with cl100k_base. Tokenization choices affect model performance on different languages.",
        related: ["BPE", "WordPiece", "Vocabulary", "Embedding"],
      },
      {
        id: "prompt-engineering",
        term: "Prompt Engineering",
        shortDef:
          "Crafting LLM inputs to elicit desired outputs without changing model weights.",
        definition:
          'Prompt engineering leverages the in-context learning ability of LLMs: the model\'s behavior changes based on the phrasing, examples, and instructions in the prompt alone. Techniques include zero-shot ("List 3 examples of…"), few-shot (providing examples), chain-of-thought ("Let\'s think step by step"), role assignment ("You are an expert…"), and output format specification. It is a critical skill for getting reliable outputs from large language models.',
        related: [
          "In-Context Learning",
          "Chain-of-Thought",
          "Few-shot Learning",
          "LLM",
        ],
      },
      {
        id: "fine-tuning",
        term: "Fine-Tuning",
        shortDef:
          "Continuing to train a pretrained model on a new, smaller dataset to specialize its behavior.",
        definition:
          "Fine-tuning updates (some or all) model weights on a task-specific dataset, starting from pretrained weights. Full fine-tuning updates all parameters but is expensive. Parameter-efficient methods like LoRA, QLoRA, and prefix tuning update only a small fraction of parameters, dramatically reducing compute and memory. Instruction fine-tuning trains models to follow natural language instructions. RLHF is a form of fine-tuning using human preference signals.",
        related: ["Transfer Learning", "LoRA", "RLHF", "Instruction Tuning"],
      },
      {
        id: "rlhf",
        term: "RLHF",
        shortDef:
          "Reinforcement Learning from Human Feedback — aligns model outputs with human preferences.",
        definition:
          "RLHF (Christiano et al., 2017; popularized by InstructGPT 2022) consists of three stages: supervised fine-tuning on demonstrations, training a reward model on human preference comparisons, and optimizing the main model with PPO to maximize the reward model's score while staying close to the original model (KL divergence penalty). This technique transformed raw LLMs into instruction-following assistants. DPO (Direct Preference Optimization) is a simpler, reward-model-free alternative.",
        related: [
          "Fine-tuning",
          "PPO",
          "Reward Model",
          "DPO",
          "Constitutional AI",
        ],
      },
      {
        id: "in-context-learning",
        term: "In-Context Learning",
        shortDef:
          "LLMs learn from examples provided in the prompt without any gradient updates.",
        definition:
          "In-context learning (ICL) is the ability of large models to adapt to a new task purely by conditioning on input-output examples in the prompt — no weight update occurs. A few-shot prompt might show 3-5 labeled examples before the actual query. ICL emerges at scale and is largely absent in small models. The mechanism is debated: some evidence suggests models perform induction (pattern matching) while others suggest implicit Bayesian inference.",
        related: [
          "Few-shot Learning",
          "Prompt Engineering",
          "Emergent Abilities",
        ],
      },
      {
        id: "chain-of-thought",
        term: "Chain-of-Thought (CoT)",
        shortDef:
          "Prompting LLMs to reason step-by-step before giving a final answer, improving accuracy.",
        definition:
          'Wei et al. (2022) showed that adding "Let\'s think step by step" or exemplars with reasoning traces dramatically improves LLM performance on math, logic, and multi-step problems. CoT leverages the model\'s token generation: laying out reasoning explicitly guides it toward correct conclusions. Zero-shot CoT ("think step by step") works without examples. Tree-of-Thought and Self-Consistency extend the idea by exploring or sampling multiple reasoning paths.',
        related: ["Prompt Engineering", "Reasoning", "LLM", "ReAct Pattern"],
      },
      {
        id: "context-window",
        term: "Context Window",
        shortDef:
          "The maximum number of tokens an LLM can process at once — its working memory.",
        definition:
          "The context window defines how much text (measured in tokens) a model can attend to in a single forward pass. GPT-4 supports ~128K tokens; Claude 3.5 supports 200K; Gemini 1.5 Pro reaches 1M. A larger context allows processing entire codebases, books, or conversation histories at once. However, models often struggle with information buried in the middle of very long contexts — the 'lost in the middle' problem. KV cache size scales with context length, creating significant memory constraints at deployment.",
        related: ["KV Cache", "Positional Encoding", "Long Context", "LLM"],
      },
      {
        id: "kv-cache",
        term: "KV Cache",
        shortDef:
          "Cached key-value pairs from previous tokens to avoid recomputing attention each step.",
        definition:
          "During autoregressive generation, every new token needs attention over all previous tokens. Without caching, this requires recomputing key and value projections for all past tokens at every step — O(n²) per-step cost. KV caching stores these projections in memory and reuses them, reducing per-step cost to O(1) per new token. Memory consumption scales as O(n × layers × d_model), limiting maximum context length. Grouped Query Attention (GQA) and Multi-Query Attention (MQA) reduce this memory overhead by sharing keys/values across attention heads.",
        related: [
          "Context Window",
          "Attention Mechanism",
          "Inference Optimization",
          "Speculative Decoding",
        ],
      },
      {
        id: "lora",
        term: "LoRA (Low-Rank Adaptation)",
        shortDef:
          "Fine-tuning by adding tiny trainable low-rank matrices to frozen weights — efficient and cheap.",
        definition:
          "LoRA (Hu et al., 2021) freezes the original model weights and injects pairs of small trainable matrices A and B into attention layers such that the effective weight update ΔW = BA, where rank r ≪ original dimensions. For a 7B model, this reduces trainable parameters from billions to millions. QLoRA further quantizes the frozen model to 4-bit, enabling fine-tuning of 65B+ models on a single consumer GPU. LoRA adapters can be merged into the base model for zero inference overhead or swapped at runtime to serve multiple personas from one base model.",
        related: [
          "Fine-Tuning",
          "Quantization",
          "RLHF",
          "Knowledge Distillation",
        ],
      },
      {
        id: "quantization",
        term: "Quantization",
        shortDef:
          "Reducing weight precision from 32-bit floats to fewer bits for smaller, faster models.",
        definition:
          "Quantization maps high-precision float weights (FP32/BF16) to lower-precision formats (INT8, INT4, FP8). Post-training quantization (PTQ) methods like GPTQ, AWQ, and GGUF reduce model memory by 4-8× with minimal accuracy loss. QLoRA uses 4-bit NormalFloat (NF4) for frozen base weights during fine-tuning. GGUF is a portable format for running quantized models locally via llama.cpp. Quantization-aware training (QAT) fine-tunes models to be robust to lower precision from the start, generally achieving better accuracy than PTQ.",
        related: [
          "LoRA",
          "Inference Optimization",
          "GGUF",
          "Knowledge Distillation",
        ],
      },
      {
        id: "dpo",
        term: "DPO (Direct Preference Optimization)",
        shortDef:
          "Preference alignment without a separate reward model — optimizes on preference pairs directly.",
        definition:
          "DPO (Rafailov et al., 2023) shows that the RLHF objective can be optimized directly without training a separate reward model. Given pairs of preferred/rejected responses, DPO reparameterizes the reward in terms of the policy itself and derives a simple binary cross-entropy loss. This is more stable than PPO, requires no reward model, and achieves comparable or better alignment. SimPO, ORPO, and KTO are follow-up variants. Most modern instruction-tuned models (Mistral-Instruct, Llama 3, Gemma) use DPO or a variant for their alignment stage.",
        related: ["RLHF", "Fine-tuning", "Constitutional AI", "GRPO"],
      },
      {
        id: "scaling-laws",
        term: "Scaling Laws",
        shortDef:
          "Empirical relationships showing model loss improves predictably with compute, data, and parameters.",
        definition:
          "Kaplan et al. (2020) found that language model loss follows smooth power laws with model size N, dataset size D, and compute budget C. The Chinchilla paper (Hoffmann 2022) revised these laws, showing most models were undertrained — optimal scaling uses roughly 20 tokens per parameter. These laws enable predicting performance before training and guide compute allocation decisions. Important caveats: scaling laws describe average cross-entropy loss, not emergent capabilities, and shift with architecture improvements or data quality changes. Inference-time (test-time) scaling is the newest dimension being explored.",
        related: [
          "Emergent Abilities",
          "LLM",
          "Reasoning Models",
          "Compute Efficiency",
        ],
      },
      {
        id: "temperature-sampling",
        term: "Temperature & Sampling",
        shortDef:
          "Controls output randomness — temperature scales logits; top-p and top-k restrict candidates.",
        definition:
          "Temperature T divides the raw logits before softmax: T < 1 makes the distribution peakier (more deterministic), T = 0 is greedy decoding, T > 1 flattens it (more creative/random). Top-k sampling restricts the next token choice to the k highest-probability words. Top-p (nucleus) sampling dynamically selects the smallest set of tokens whose cumulative probability exceeds p. These three parameters are the primary inference knobs for trading off creativity vs. accuracy. Low temperature is used for code and factual tasks; higher temperature for brainstorming and creative writing.",
        related: [
          "Autoregressive",
          "LLM",
          "Prompt Engineering",
          "Reasoning Models",
        ],
      },
      {
        id: "system-prompt",
        term: "System Prompt",
        shortDef:
          "The hidden instruction prepended to every conversation that sets the model's persona and rules.",
        definition:
          "Modern chat LLMs support a system prompt — a privileged instruction block that precedes user messages and shapes model behavior throughout the conversation. It can assign a persona ('You are an expert Python engineer'), enforce constraints ('Always respond in JSON'), or inject contextual knowledge. System prompts are typically not shown to users but influence every response. Prompt injection attacks attempt to override system prompt instructions through crafted user inputs. Robust system prompt design is critical for building reliable, safe AI applications.",
        related: [
          "Prompt Engineering",
          "Instruction Tuning",
          "In-Context Learning",
          "LLM",
        ],
      },
    ],
    "transformers",
  ),

  // ── Generative AI ───────────────────────────────────────────
  ...withCategory(
    [
      {
        id: "gan",
        term: "GAN (Generative Adversarial Network)",
        shortDef:
          "Two-network game: a generator creates samples, a discriminator judges real vs. fake.",
        definition:
          "GANs (Goodfellow et al., 2014) consist of a generator G and discriminator D trained adversarially. G tries to produce samples indistinguishable from real data; D tries to distinguish real from generated. They are trained simultaneously in a minimax game until G fools D. GANs produce extremely sharp images but suffer from training instability and mode collapse. Variants (StyleGAN, BigGAN, CycleGAN) addressed these issues, and GANs dominated image synthesis until diffusion models surpassed them.",
        related: ["Diffusion Model", "VAE", "Latent Space", "Image Synthesis"],
      },
      {
        id: "vae",
        term: "Variational Autoencoder (VAE)",
        shortDef:
          "Generative model that learns a structured probabilistic latent space for smooth interpolation.",
        definition:
          "VAEs (Kingma & Welling, 2013) encode inputs to a distribution over latent vectors (mean + variance), sample from it, and decode the sample back. The training objective (ELBO) balances reconstruction quality with regularizing the latent space to be Gaussian. The stochastic latent space allows smooth interpolation and generation. VAEs produce blurrier images than GANs but are more stable to train. Latent diffusion models use a VAE to compress images before diffusion.",
        related: ["GAN", "Latent Space", "Diffusion Model", "Encoder-Decoder"],
      },
      {
        id: "diffusion-model",
        term: "Diffusion Model",
        shortDef:
          "Generative model that learns to reverse a gradual noise-adding process to synthesize data.",
        definition:
          "Diffusion models (DDPM, Ho et al., 2020) define a forward process that gradually adds Gaussian noise to data over T steps until it becomes pure noise. A neural network learns the reverse process: denoise step by step to recover a sample. At inference, starting from random noise, the model iteratively denoises to generate high-quality images. Stable Diffusion, DALL-E 2/3, and Midjourney are based on this principle. Text conditioning is injected via cross-attention.",
        related: [
          "Latent Diffusion",
          "DDPM",
          "Score Matching",
          "VAE",
          "Text-to-Image",
        ],
      },
      {
        id: "latent-space",
        term: "Latent Space",
        shortDef:
          "Compressed internal representation space learned by an encoder from raw data.",
        definition:
          'A latent space is the lower-dimensional representation that an encoder maps inputs into. Points in this space capture semantic meaning: similar items cluster together, and arithmetic operations can yield meaningful results (e.g., king − man + woman ≈ queen in word embeddings). VAEs and diffusion models with VAE encoders operate in latent space. The "quality" of a latent space — how smooth, structured, and disentangled it is — determines the quality of generated samples.',
        related: ["VAE", "Embedding", "Encoder-Decoder", "Disentanglement"],
      },
      {
        id: "text-to-image",
        term: "Text-to-Image Generation",
        shortDef:
          "Models that generate images conditioned on natural language descriptions.",
        definition:
          "Text-to-image models (DALL-E, Stable Diffusion, Midjourney, Imagen) use text encoders (CLIP, T5) to embed prompts, then condition a diffusion or autoregressive process on those embeddings. CLIP jointly trains an image and text encoder on image-caption pairs, learning a shared semantic space. Classifier-Free Guidance amplifies how strongly the model follows the text prompt. These models are now capable of photorealistic, artistic, and creative image synthesis at arbitrary styles.",
        related: [
          "Diffusion Model",
          "CLIP",
          "Text Conditioning",
          "Stable Diffusion",
        ],
      },
      {
        id: "stable-diffusion",
        term: "Stable Diffusion",
        shortDef:
          "Open-source latent diffusion model that performs image synthesis in compressed latent space.",
        definition:
          "Stable Diffusion (Rombach et al., 2022) runs the diffusion process in a compressed latent space (8× smaller than pixel space) using a VAE encoder/decoder, dramatically reducing memory and compute vs. pixel-space diffusion. Text conditioning is injected via cross-attention using CLIP text embeddings. Classifier-Free Guidance (CFG) amplifies text adherence. Being fully open-source, SD spawned an ecosystem of fine-tuned variants: DreamBooth personalizes on ~5 images, Textual Inversion learns new concepts, ControlNet adds spatial conditioning, and SDXL/Flux extend it further.",
        related: [
          "Diffusion Model",
          "Latent Space",
          "ControlNet",
          "Text-to-Image",
          "VAE",
        ],
      },
      {
        id: "controlnet",
        term: "ControlNet",
        shortDef:
          "Adds spatial conditioning (edges, depth, pose) to diffusion models for fine-grained control.",
        definition:
          "ControlNet (Zhang et al., 2023) clones the encoder blocks of a diffusion UNet into a trainable branch conditioned on spatial signals — Canny edges, depth maps, human pose skeletons, segmentation masks, or line art. These conditions provide geometric control over generated images far beyond what text prompts allow. The original model weights are locked while only the control branch trains. ControlNet has been widely adopted for consistent character generation, image-to-image translation, architecture visualization, and turning rough sketches into photorealistic renders.",
        related: [
          "Stable Diffusion",
          "Diffusion Model",
          "Text-to-Image",
          "Image-to-Image",
        ],
      },
      {
        id: "video-generation",
        term: "Video Generation (Sora)",
        shortDef:
          "Diffusion Transformer models that generate minutes-long, physically coherent video from text.",
        definition:
          "Video generation extends image diffusion into the temporal dimension. Sora (OpenAI, 2024) uses a Diffusion Transformer (DiT) that treats videos as sequences of spacetime patches, enabling generation of up to 60-second cinematic clips. It demonstrates emergent understanding of physical dynamics — fluid flow, shadows, camera motion — without any physics simulation. Runway Gen-3, Google Veo 2, and Meta Movie Gen followed with comparable quality. Key enablers: spacetime VAE compression of video tokens and scaling training to internet-scale video. Long-term semantic consistency remains an open challenge.",
        related: [
          "Diffusion Model",
          "Text-to-Image",
          "Transformer Architecture",
          "Multimodal AI",
        ],
      },
    ],
    "generative",
  ),

  // ── RAG & Knowledge ─────────────────────────────────────────
  ...withCategory(
    [
      {
        id: "rag",
        term: "Retrieval-Augmented Generation (RAG)",
        shortDef:
          "LLMs that retrieve relevant documents at query time and use them as grounding context.",
        definition:
          "RAG (Lewis et al., 2020) combines retrieval and generation: given a query, a retriever fetches relevant documents from a knowledge base, and the LLM generates an answer conditioned on those documents. This grounds the model in current, verifiable information, reducing hallucination. Unlike fine-tuning, RAG allows knowledge updates without retraining. The knowledge base is typically a vector database of embedded text chunks. Modern extensions include Agentic RAG, Corrective RAG, and Self-RAG.",
        related: [
          "Vector Database",
          "Embedding",
          "Hallucination",
          "Contextual Retrieval",
          "Agentic RAG",
        ],
      },
      {
        id: "vector-database",
        term: "Vector Database",
        shortDef:
          "Specialized database for storing and searching dense vector embeddings by semantic similarity.",
        definition:
          'Vector databases index high-dimensional embedding vectors and support approximate nearest-neighbor (ANN) search algorithms (HNSW, IVF, PQ) for fast semantic retrieval. Popular options include Pinecone, Weaviate, Qdrant, Chroma, and Milvus. They typically support metadata filtering (e.g., "only search papers from 2024"), hybrid search (combining dense + sparse), and namespace/tenant isolation. They are the retrieval backbone of every RAG system.',
        related: ["Embedding", "Semantic Search", "ANN Search", "HNSW", "RAG"],
      },
      {
        id: "embedding",
        term: "Embedding",
        shortDef:
          "Dense vector representation of text (or other data) in a semantic latent space.",
        definition:
          "An embedding model converts text into a fixed-size float vector (e.g., 768 or 1536 dimensions) such that semantically similar texts have high cosine similarity. Embedding models are trained via contrastive learning on similar/dissimilar pairs. SBERT, E5, BGE, and OpenAI's text-embedding models are popular choices. Embedding quality directly determines retrieval quality in RAG systems. Embedding the same concept in multiple languages ideally places them near each other (multilingual embeddings).",
        related: [
          "Semantic Search",
          "Vector Database",
          "Contrastive Learning",
          "Cosine Similarity",
        ],
      },
      {
        id: "semantic-search",
        term: "Semantic Search",
        shortDef:
          "Search by meaning rather than exact keyword matching, powered by embedding similarity.",
        definition:
          "In semantic search, both query and documents are embedded into the same vector space. Retrieval finds documents whose embedding is closest (by cosine or dot product) to the query embedding. This handles synonyms, paraphrases, and conceptual matches that keyword search misses. However, it can miss exact lexical matches and technical identifiers (model names, paper IDs) — which is why hybrid search (combining semantic + BM25) often outperforms either alone.",
        related: ["Embedding", "Vector Database", "BM25", "Hybrid Search"],
      },
      {
        id: "chunking",
        term: "Chunking",
        shortDef:
          "Splitting long documents into smaller pieces before embedding, so retrieval granularity is useful.",
        definition:
          "LLMs and embedding models have fixed context windows. Chunking breaks documents into pieces of 256–1024 tokens so they can be embedded and retrieved individually. Fixed-size chunking is simple but can split mid-sentence. Sentence or paragraph chunking preserves semantic units. Hierarchical chunking stores both small chunks (for precision) and their parent sections (for context). Chunk size is a critical hyperparameter: too small loses context; too large dilutes signal.",
        related: [
          "RAG",
          "Embedding",
          "Contextual Retrieval",
          "Parent Document Retriever",
        ],
      },
      {
        id: "contextual-retrieval",
        term: "Contextual Retrieval",
        shortDef:
          "LLM-generated context is prepended to each chunk before embedding to reduce retrieval failure.",
        definition:
          "Anthropic (2024) showed that without document-level context, retrieved chunks are often uninterpretable — they lack knowledge of which paper, section, or argument they come from. Contextual Retrieval first uses an LLM to generate a 2-3 sentence description situating each chunk within its document, prepends this context, then embeds. This reduced retrieval failure by ~49% in benchmarks. The context is generated once at ingestion time, so there is no additional cost at query time.",
        related: ["RAG", "Chunking", "Contextual Compression", "Embedding"],
      },
      {
        id: "hybrid-search",
        term: "Hybrid Search",
        shortDef:
          "Combining dense (semantic) and sparse (keyword/BM25) retrieval scores for better coverage.",
        definition:
          'Pure dense retrieval misses exact lexical matches; pure sparse retrieval misses semantic matches. Hybrid search runs both and fuses scores using Reciprocal Rank Fusion (RRF) or weighted sum. Dense captures "papers about self-supervised learning"; sparse catches "SimCLR 2020" exactly. Most production RAG systems use hybrid search. Pinecone, Weaviate, and Elasticsearch all support hybrid natively. This typically improves recall by 10-30% over either method alone.',
        related: ["Semantic Search", "BM25", "Vector Database", "RRF"],
      },
      {
        id: "bm25",
        term: "BM25",
        shortDef:
          "Probabilistic sparse keyword retrieval algorithm — the foundation of Elasticsearch/Lucene.",
        definition:
          "BM25 (Best Match 25) scores documents based on term frequency and inverse document frequency, with saturation to prevent very frequent terms from dominating. It is the default ranking function in Elasticsearch, Solr, and most search engines. Despite being decades old, BM25 remains competitive for precise keyword matching. In hybrid RAG systems, BM25 handles exact model names, paper titles, author names, and acronyms that dense retrieval often gets wrong.",
        related: [
          "Hybrid Search",
          "TF-IDF",
          "Sparse Retrieval",
          "Elasticsearch",
        ],
      },
    ],
    "rag",
  ),

  // ── Agents ──────────────────────────────────────────────────
  ...withCategory(
    [
      {
        id: "ai-agent",
        term: "AI Agent",
        shortDef:
          "An LLM-based system that autonomously plans, uses tools, and takes multi-step actions toward a goal.",
        definition:
          'An AI agent perceives its environment, reasons about it, and takes actions — often using tools like web search, code execution, or database queries. The LLM is the "brain" that plans and decides; tools provide the ability to act. Agents can run loops: observe → think → act → observe → repeat. This makes them capable of complex multi-step tasks that require world knowledge and tool use. Key frameworks include LangGraph, CrewAI, AutoGen, and Microsoft\'s Semantic Kernel.',
        related: [
          "Tool Use",
          "ReAct Pattern",
          "LangGraph",
          "Agentic RAG",
          "Function Calling",
        ],
      },
      {
        id: "tool-use",
        term: "Tool Use",
        shortDef:
          "LLMs calling external functions (APIs, databases, code runners) to extend their capabilities.",
        definition:
          "Tool-augmented LLMs can search the web, query databases, execute code, read files, and call APIs. The model is given tool descriptions and decides when and how to call them. Tool outputs are fed back to the model as context. This turns a static knowledge model into a dynamic agent. OpenAI's function calling, Anthropic's tool use, and LangChain tools are popular implementations. Careful tool design and grounding matters — LLMs will hallucinate tool arguments if not carefully constrained.",
        related: [
          "Function Calling",
          "AI Agent",
          "ReAct Pattern",
          "Code Interpreter",
        ],
      },
      {
        id: "react-pattern",
        term: "ReAct Pattern",
        shortDef:
          "Interleaves Reasoning and Acting — LLM alternates between thinking and tool calls.",
        definition:
          'ReAct (Yao et al., 2022) prompts the model to generate a "Thought" → "Action" (tool call) → "Observation" (tool result) cycle, repeating until it reaches a final answer. The explicit reasoning trace improves decision quality and interpretability. It is the basis for most agent implementations today. LangGraph generalizes this with stateful graphs where each node can be a reasoning or action step.',
        related: ["AI Agent", "Tool Use", "Chain-of-Thought", "LangGraph"],
      },
      {
        id: "langgraph",
        term: "LangGraph",
        shortDef:
          "Framework for building stateful, graph-based LLM agent workflows with cycle support.",
        definition:
          "LangGraph defines agentic workflows as directed graphs where nodes represent computation steps (LLM calls, tool invocations) and edges represent transitions (conditional or unconditional). Unlike simple chains, LangGraph supports cycles — the agent can loop, retry, or route to different nodes based on output. State is typed and passed through the graph. It implements patterns like CRAG (Corrective RAG), Self-RAG, and multi-agent systems. The Python and JS/TS versions are both available.",
        related: [
          "AI Agent",
          "ReAct Pattern",
          "Agentic RAG",
          "Stateful Workflows",
        ],
      },
      {
        id: "function-calling",
        term: "Function Calling",
        shortDef:
          "LLMs generating structured JSON to invoke predefined functions rather than freeform text.",
        definition:
          "OpenAI's function calling (2023) allows models to output structured JSON matching a provided schema when they decide a tool should be invoked. This is more reliable than asking the model to format tool calls in its text output. The model can choose from multiple tools and provide correctly-typed arguments. Modern extensions (Anthropic tool use, Google function declarations) use the same paradigm. Parallel function calling allows multiple tools to be invoked in one turn.",
        related: ["Tool Use", "AI Agent", "JSON Schema", "Structured Output"],
      },
      {
        id: "agentic-rag",
        term: "Agentic RAG",
        shortDef:
          "RAG system that autonomously grades retrieval quality and rewrites queries to improve answers.",
        definition:
          "Agentic RAG (Self-RAG, CRAG, LangGraph-based) adds autonomous decision-making to the RAG pipeline. After retrieving documents, an LLM grades each for relevance. If quality is too low, the agent can rewrite the query and retrieve again, search a different source, or route to a general LLM fallback. A guardrail node filters off-topic queries before any retrieval. Reasoning steps are logged and can be shown to users for transparency. This significantly improves answer quality on complex or ambiguous questions.",
        related: ["RAG", "LangGraph", "Self-RAG", "CRAG", "Document Grading"],
      },
      {
        id: "mcp",
        term: "Model Context Protocol (MCP)",
        shortDef:
          "Anthropic's open standard for connecting LLMs to external tools, data, and services.",
        definition:
          "MCP (Anthropic, 2024) defines a universal protocol for integrating AI assistants with external resources — files, databases, APIs, browser automation, code execution, and more. It uses a client-server architecture: MCP servers expose resources and tools; the LLM host connects to them. This solves integration fragmentation — instead of each application building custom tool connectors, any MCP-compliant tool plugs into any MCP-compatible AI. Quickly adopted by VS Code, Cursor, Claude Desktop, Windsurf, and hundreds of developer tools. MCP is to AI agents what USB is to hardware peripherals.",
        related: ["Tool Use", "Function Calling", "AI Agent", "LangGraph"],
      },
      {
        id: "multi-agent",
        term: "Multi-Agent Systems",
        shortDef:
          "Multiple specialized LLM agents collaborating — planner, executor, critic — on complex tasks.",
        definition:
          "Multi-agent frameworks (AutoGen, CrewAI, LangGraph) assign specialized roles to individual LLM instances: a planner breaks down goals, executor agents implement sub-tasks, validator agents verify outputs, and a critic reviews the whole. Agents communicate through a shared message bus or structured handoffs. This mirrors human team dynamics and enables parallelizing work, applying multiple specialized capabilities, or implementing checks and balances. Key challenges include error propagation between agents, communication overhead, and prompt injection attacks through inter-agent messages.",
        related: ["AI Agent", "LangGraph", "Tool Use", "Agentic RAG", "MCP"],
      },
      {
        id: "guardrails",
        term: "Guardrails",
        shortDef:
          "Safety and validation layers around LLM outputs — classifying, filtering, and correcting responses.",
        definition:
          "Guardrails are programmatic checks applied before, during, or after LLM generation to enforce safety and quality. Input guardrails detect and block prompt injection, jailbreak attempts, or off-topic queries. Output guardrails validate format (is JSON valid?), content safety (no toxic output), and factual grounding (is the response supported by retrieved documents?). Frameworks like GuardrailsAI, NeMo Guardrails, and LlamaGuard provide structured implementations. In agentic systems, guardrails are especially critical because errors compound across steps and may trigger irreversible actions.",
        related: [
          "AI Agent",
          "Agentic RAG",
          "Hallucination",
          "Constitutional AI",
          "Tool Use",
        ],
      },
    ],
    "agents",
  ),

  // ── Evaluation ──────────────────────────────────────────────
  ...withCategory(
    [
      {
        id: "perplexity",
        term: "Perplexity",
        shortDef:
          "How surprised a language model is by test text — lower is better.",
        definition:
          "Perplexity measures how well a language model predicts a held-out text sequence. It is the exponentiated average negative log-likelihood per token. A perplexity of 10 means the model is, on average, uniformly uncertain among 10 tokens at each step. Lower perplexity = better language model. It is commonly used to compare models on benchmark corpora but does not directly measure downstream task performance or factuality.",
        related: [
          "Language Modeling",
          "Cross-Entropy Loss",
          "Bits per Character",
        ],
      },
      {
        id: "bleu",
        term: "BLEU Score",
        shortDef:
          "Machine translation evaluation metric based on n-gram overlap between output and references.",
        definition:
          "BLEU (Bilingual Evaluation Understudy) computes the geometric mean of n-gram precision (n = 1 to 4) between a model's translation and one or more reference translations, with a brevity penalty. Scores range 0–100; higher is better. Despite widespread use, BLEU correlates poorly with human judgment for complex translations and ignores fluency and meaning. Modern evaluation increasingly uses BERTScore, COMET, or human evaluation for translation tasks.",
        related: ["ROUGE Score", "Machine Translation", "N-gram", "BERTScore"],
      },
      {
        id: "rouge",
        term: "ROUGE Score",
        shortDef:
          "Summarization evaluation metric based on recall of n-grams from reference summaries.",
        definition:
          "ROUGE (Recall-Oriented Understudy for Gisting Evaluation) measures the overlap between a generated summary and reference summaries. ROUGE-N counts n-gram overlap; ROUGE-L measures the longest common subsequence. Unlike BLEU (precision-focused), ROUGE emphasizes recall — important for summarization where you want to capture key information. ROUGE is fast and automatic but misses paraphrasing and semantic equivalence that do not share literal n-grams.",
        related: ["BLEU Score", "Summarization", "N-gram", "BERTScore"],
      },
      {
        id: "hallucination",
        term: "Hallucination",
        shortDef:
          "When an LLM generates factually incorrect, unverifiable, or invented information with high confidence.",
        definition:
          "LLMs hallucinate when they produce statements that are plausible in form but factually wrong, non-existent, or unverifiable — yet the model asserts them confidently. Closed-book hallucinations invent facts lacking in training data. RAG hallucinations occur when the model ignores or contradicts retrieved sources. Causes include next-token prediction objective (coherence over truth), limited world knowledge, and distributional shift. Mitigation: RAG, chain-of-thought, self-consistency, fine-tuning on factual tasks, and citation verification.",
        related: ["RAG", "Grounding", "Factuality", "Constitutional AI"],
      },
      {
        id: "grounding",
        term: "Grounding",
        shortDef:
          "Tying LLM outputs to verifiable sources so factual claims can be checked.",
        definition:
          "A grounded response provides explicit citations or quotes from source documents that support each claim. RAG is the primary grounding technique — the model can only state what appears in retrieved documents. Citation-based grounding (like Bing Chat's footnotes) lets users verify statements. Grounding does not guarantee correctness (the source could be wrong) but does make hallucination detectable. Attribution training fine-tunes models to produce verifiable in-text citations.",
        related: ["RAG", "Hallucination", "Attribution", "Factuality"],
      },
    ],
    "evaluation",
  ),

  // ── Cutting Edge ────────────────────────────────────────────
  ...withCategory(
    [
      {
        id: "moe",
        term: "Mixture of Experts (MoE)",
        shortDef:
          'Architecture with many specialized sub-networks, routing each token to only a few "experts".',
        definition:
          "MoE layers replace a single feed-forward network with N expert networks and a router that selects k of them (usually k=2) for each token. Only those k experts are computed, reducing FLOPs per token despite huge total parameter counts. Mistral's Mixtral 8x7B, GPT-4 (rumored), and Google's Switch Transformer use this. MoE models achieve higher capacity with the same compute budget but add training instability (expert collapse) and routing complexity.",
        related: [
          "Sparse Computation",
          "Routing",
          "Scaling Laws",
          "Transformer Architecture",
        ],
      },
      {
        id: "multimodal",
        term: "Multimodal AI",
        shortDef:
          "AI systems that understand and generate across multiple modalities: text, image, audio, video.",
        definition:
          "Multimodal models process and relate information across modalities. CLIP learns joint image-text embeddings. GPT-4V, Gemini, and Claude 3 can reason about images in natural language. Flamingo, LLaVA, and Qwen-VL use a visual encoder plus connector to inject visual tokens into an LLM. Beyond vision-language, multimodal systems increasingly handle audio (Whisper), video, 3D, and structured data. The convergence toward unified models that handle all modalities is a key frontier.",
        related: ["Vision Language Model", "CLIP", "GPT-4V", "Text-to-Image"],
      },
      {
        id: "vlm",
        term: "Vision Language Model (VLM)",
        shortDef:
          "Model combining a vision encoder and LLM to understand images with natural language.",
        definition:
          "VLMs encode images with a pretrained vision encoder (ViT, CLIP), project visual tokens into the LLM's embedding space via an MLP or cross-attention connector, and feed them alongside text tokens to the LLM. This enables image-based question answering, captioning, OCR, diagram understanding, and visual reasoning. Training mixes image-text pairs and instruction data. LLaVA, InternVL, Qwen-VL, Phi-3-Vision, and GPT-4V are prominent examples.",
        related: ["Multimodal AI", "ViT", "CLIP", "LLM"],
      },
      {
        id: "ssm",
        term: "State Space Model (SSM)",
        shortDef:
          "Sequence model based on linear dynamical systems, efficient for very long sequences.",
        definition:
          "State Space Models (S4, H3, Mamba) represent sequences as linear recurrences with learned state transition matrices. Unlike Transformers, SSMs have O(n) complexity in sequence length rather than O(n²), making them tractable for 1M+ token sequences. They can be computed as convolutions (parallel training) or recurrences (fast inference). Mamba adds selective state spaces with input-dependent gating. SSMs challenge Transformer dominance for long-context tasks like genomics, audio, and long documents.",
        related: [
          "Mamba Architecture",
          "Transformer Architecture",
          "Long Context",
          "Recurrence",
        ],
      },
      {
        id: "mamba",
        term: "Mamba Architecture",
        shortDef:
          "Selective state space model that dynamically weights what to remember based on the input.",
        definition:
          "Mamba (Gu & Dao, 2023) extends SSMs with selective state spaces — the state transition parameters are functions of the input, allowing the model to choose what information to propagate or forget. This gives it the modeling power of attention without the O(n²) cost. Mamba-2 introduced state space duality showing theoretical connections to linear attention. Hybrid architectures (Zamba, Jamba) interleave Mamba and attention layers for best of both worlds.",
        related: [
          "State Space Model",
          "Linear Attention",
          "Long Context",
          "Efficiency",
        ],
      },
      {
        id: "constitutional-ai",
        term: "Constitutional AI",
        shortDef:
          "Anthropic's method for aligning LLMs using a set of principles and self-critique.",
        definition:
          "Constitutional AI (Anthropic, 2022) trains models to be helpful, harmless, and honest by having them critique and revise their own responses according to a predefined constitution (a set of principles). In the SL-CAI stage, the model self-critiques using the constitution. In the RL-CAI stage, preference labels are generated by a model rather than humans, dramatically scaling RLHF without proportional human annotation costs. This method inspired Principle-based alignment and self-play fine-tuning approaches.",
        related: ["RLHF", "Alignment", "Self-Critique", "DPO"],
      },
      {
        id: "emergent-abilities",
        term: "Emergent Abilities",
        shortDef:
          "Capabilities that appear only above certain model scale thresholds, absent in smaller models.",
        definition:
          "Wei et al. (2022) documented abilities like multi-step arithmetic, chain-of-thought reasoning, and code generation that are near-zero in smaller models but jump sharply above scale thresholds. This phase-transition behavior is qualitatively different from smooth scaling curves for standard metrics. Critics (Schaeffer et al., 2023) argue emergence is an artifact of metric choice — switching to continuous metrics often shows smooth improvement. Whether emergence is real or apparent has significant implications for AI forecasting.",
        related: [
          "Scaling Laws",
          "LLM",
          "In-Context Learning",
          "Phase Transitions",
        ],
      },
      {
        id: "federated-learning",
        term: "Federated Learning",
        shortDef:
          "Training ML models across decentralized devices without centralizing raw data.",
        definition:
          "Federated Learning (McMahan et al., 2017) trains a global model by aggregating gradient updates from participating devices — phones, hospitals, edge nodes — without sending raw data to a central server. FedAvg averages model weights from each client after local training. This preserves data privacy but introduces challenges: non-IID data across devices, communication bottlenecks, and clients dropping out mid-round. Used by Google Keyboard (Gboard) for on-device personalization without uploading typing data.",
        related: [
          "Privacy-Preserving ML",
          "Differential Privacy",
          "Distributed Learning",
        ],
      },
      {
        id: "neurosymbolic",
        term: "Neurosymbolic AI",
        shortDef:
          "Combining neural networks (pattern learning) with symbolic reasoning (rules and logic).",
        definition:
          "Neurosymbolic AI aims to integrate the generalization power of neural networks with the precision and interpretability of symbolic AI (logic, knowledge graphs, rule systems). Neural-guided symbolic search, differentiable programming, and logical neural networks are some approaches. It addresses pure neural models' weaknesses in systematic generalization, compositional reasoning, and knowledge representation. AlphaProof (DeepMind, 2024) combined LLMs with Lean formal proof verification to solve Math Olympiad problems.",
        related: [
          "Symbolic AI",
          "Knowledge Graph",
          "Formal Verification",
          "Compositional Reasoning",
        ],
      },
      {
        id: "reasoning-models",
        term: "Reasoning Models (o1 / R1 Style)",
        shortDef:
          "LLMs that generate extended internal thinking chains before answering, trained with RL.",
        definition:
          "OpenAI's o1 (2024) introduced a new paradigm: train models with reinforcement learning to produce long 'thinking' traces before giving final answers. The model explores approaches, backtracks, and self-corrects in hidden chain-of-thought steps. DeepSeek-R1 replicated this using GRPO without human-labeled reasoning chains. QwQ-32B showed smaller models can reach o1-level reasoning. Test-time compute scaling — allocating more 'thinking' tokens — consistently improves accuracy on math, code, and science benchmarks. This shifted focus from scaling pretraining compute to scaling inference compute.",
        related: [
          "GRPO",
          "Chain-of-Thought",
          "Scaling Laws",
          "RLHF",
          "Speculative Decoding",
        ],
      },
      {
        id: "grpo",
        term: "GRPO (Group Relative Policy Optimization)",
        shortDef:
          "RL for LLMs without a critic network — uses group-sampled rewards as a relative baseline.",
        definition:
          "GRPO (DeepSeek, 2024) is the RL algorithm behind DeepSeek-R1 and other reasoning models. Unlike PPO which requires a separate critic (value function) network, GRPO samples G responses for each prompt and uses their average reward as a baseline for each. This eliminates the critic entirely, halving memory requirements. Combined with rule-based verifiable rewards (math answer correctness, code unit test pass rate), GRPO efficiently trains emergent reasoning behaviors — including the 'aha moment' where the model learns to backtrack and reconsider. It enables reasoning training at far lower cost than PPO.",
        related: [
          "Reasoning Models",
          "RLHF",
          "DPO",
          "Reinforcement Learning",
          "Reward Model",
        ],
      },
      {
        id: "speculative-decoding",
        term: "Speculative Decoding",
        shortDef:
          "Faster LLM inference using a small draft model to propose tokens verified by the large model.",
        definition:
          "Speculative decoding (Chen et al. / Leviathan et al., 2023) uses a small draft model to generate k candidate tokens in parallel, which the large target model then verifies in one forward pass. Tokens whose distribution matches are accepted; the first mismatch is resampled and later tokens are discarded. This delivers 2-3× wall-clock speedup with mathematically identical output distribution to the original model. Used in production by Google (Gemini), Anthropic (Claude), and others. Medusa extends this using multiple parallel prediction heads on the same model rather than a separate draft model.",
        related: [
          "Inference Optimization",
          "KV Cache",
          "Token Generation",
          "LLM",
        ],
      },
      {
        id: "knowledge-distillation",
        term: "Knowledge Distillation",
        shortDef:
          "Training a small 'student' model to mimic the outputs or internals of a large 'teacher' model.",
        definition:
          "Knowledge distillation (Hinton et al., 2015) trains a compact student to match the soft probability distributions (logits) of a larger teacher, rather than just hard labels. Soft targets carry rich inter-class similarity information that hard-label training loses. Response distillation matches final outputs; feature distillation matches hidden representations. Modern LLM distillation trained smaller models (Phi-3, Gemma 2, Qwen) from teacher-generated synthetic data, achieving near-large-model quality at a fraction of the parameters. Key risk: knowledge distillation from closed models may violate their terms of service.",
        related: ["Quantization", "LoRA", "Synthetic Data", "Fine-tuning"],
      },
      {
        id: "synthetic-data",
        term: "Synthetic Data",
        shortDef:
          "AI-generated training data used to augment or replace human-labeled datasets.",
        definition:
          "Synthetic data uses powerful foundation models to generate training examples for downstream tasks or smaller models. Techniques include self-instruct (GPT-4 generates instruction-following pairs), rejection sampling (generating many responses, keeping those a verifier approves), and model-written critiques. Phi-1 and Phi-3 (Microsoft) trained on 'textbook-quality' synthetic data, achieving performance far above their parameter count. Key risk: model collapse — when a model trains iteratively on its own synthetic outputs, errors compound and diversity collapses across generations, degrading output quality.",
        related: [
          "Knowledge Distillation",
          "Instruction Tuning",
          "RLHF",
          "Reasoning Models",
        ],
      },
      {
        id: "flash-attention",
        term: "Flash Attention",
        shortDef:
          "IO-aware attention algorithm that avoids materializing the full attention matrix in GPU HBM.",
        definition:
          "Standard attention materializes an n×n attention matrix in GPU High Bandwidth Memory (HBM), making memory cost O(n²). Flash Attention (Dao et al., 2022) reorders matrix multiplications using tiling to keep intermediate results in fast SRAM, never writing the full matrix to HBM. This reduces memory from O(n²) to O(n) and speeds up training 2-4× with exactly the same mathematical result. Flash Attention 2 and 3 improved parallelism and GPU utilization further. Now the default attention implementation in all major training frameworks (PyTorch, JAX, Triton).",
        related: [
          "Attention Mechanism",
          "Multi-Head Attention",
          "KV Cache",
          "Inference Optimization",
        ],
      },
      {
        id: "mechanistic-interpretability",
        term: "Mechanistic Interpretability",
        shortDef:
          "Reverse-engineering neural networks to identify specific circuits that implement specific behaviors.",
        definition:
          "Mechanistic interpretability (Anthropic, EleutherAI, Neel Nanda) aims to understand neural networks at the algorithmic level: which neurons and pathways implement a given capability. Key findings include 'induction heads' (circuits enabling in-context learning), factual recall stored in MLP layers, and modular circuits for tasks like indirect object identification. Sparse Autoencoders (SAEs) decompose superposition-compressed neuronal representations into human-interpretable monosemantic features. Central to AI safety — understanding what models are doing internally may reveal misaligned objectives before they manifest in harmful behavior.",
        related: [
          "Sparse Autoencoders",
          "Constitutional AI",
          "Emergent Abilities",
          "Alignment",
        ],
      },
      {
        id: "sparse-autoencoders",
        term: "Sparse Autoencoders (SAEs)",
        shortDef:
          "Decompose neuron activations into sparse, human-interpretable features via dictionary learning.",
        definition:
          "Neural networks pack multiple concepts into single neurons via superposition. Sparse Autoencoders (Bricken et al., Anthropic 2023) learn an overcomplete dictionary of features that additively reconstruct hidden-layer activations with an L1 sparsity penalty. Each active feature corresponds to a human-interpretable concept — 'DNA strand', 'the Golden Gate Bridge', 'sycophancy tokens'. Anthropic's 'Scaling Monosemanticity' (2024) found millions of interpretable features inside Claude 3 Sonnet. SAEs are now a core tool in mechanistic interpretability and are being used to directly study and modulate model internals.",
        related: [
          "Mechanistic Interpretability",
          "Superposition",
          "Alignment",
          "Activation Engineering",
        ],
      },
      {
        id: "vibe-coding",
        term: "Vibe Coding",
        shortDef:
          "Directing an AI to build software from natural-language intent, trusting it to handle implementation details.",
        definition:
          "Coined by Andrej Karpathy (2025), vibe coding describes a workflow where the developer describes features in natural language and an AI agent (Cursor, GitHub Copilot, Claude) generates, iterates, and debugs the code with minimal manual editing. The developer 'vibes' — checking high-level correctness rather than reading every line. Enabled by instruction-following LLMs, IDE-integrated agents, and long context windows. While it dramatically accelerates prototyping, it raises questions about code understanding, security review, and technical debt accumulation when developers lack ownership of generated code.",
        related: [
          "AI Agent",
          "MCP",
          "In-Context Learning",
          "Prompt Engineering",
        ],
      },
    ],
    "cutting-edge",
  ),

  // ── Frontier Models ─────────────────────────────────────────
  ...withCategory(
    [
      {
        id: "gpt-4o",
        term: "GPT-4o",
        shortDef:
          "OpenAI's multimodal flagship — processes text, images, and audio natively in one model.",
        definition:
          "GPT-4o ('o' for omni) was released by OpenAI in May 2024 as its most capable and efficient model at the time. Unlike earlier GPT-4 variants that used separate pipelines for vision, GPT-4o natively processes text, images, and audio in a single end-to-end model, enabling real-time conversational speech with emotional awareness. It matches GPT-4 Turbo quality on text and coding while being twice as fast and half the price. The o-series models (o1, o3, o4-mini) extend this with extended reasoning capabilities trained via RL.",
        related: ["LLM", "Multimodal AI", "Reasoning Models", "OpenAI"],
      },
      {
        id: "claude",
        term: "Claude (Anthropic)",
        shortDef:
          "Anthropic's frontier model family — prioritises safety, long context, and nuanced reasoning.",
        definition:
          "Claude is Anthropic's flagship model family. Claude 3.5 Sonnet (2024) became a developer favourite for coding, analysis, and instruction-following, with a 200K token context window. Claude 3 Opus was competitive with GPT-4 on reasoning benchmarks. Anthropic trains Claude with Constitutional AI and RLHF to be helpful, harmless, and honest. Claude is available via API and Claude.ai. It was among the first commercial models to support tool use (function calling) and MCP integrations natively. Claude 3.7 Sonnet added hybrid reasoning capabilities.",
        related: ["Anthropic", "Constitutional AI", "Context Window", "LLM"],
      },
      {
        id: "gemini",
        term: "Gemini",
        shortDef:
          "Google DeepMind's multimodal model family — from ultra-scale to on-device deployment.",
        definition:
          "Gemini (Google DeepMind, 2023–) is Google's flagship foundation model series, replacing LaMDA and PaLM 2. Gemini 1.5 Pro introduced a 1M-token context window using ring attention, enabling full-length book or codebase analysis in a single call. Gemini 2.0 Flash (2025) offers near-GPT-4 quality at high speed. Gemini models power Google Search AI Overviews, Google Workspace features, and the Gemini app. Gemma is its open-weight sibling series, with Gemma 3 running on-device. Google also uses Gemma for research and fine-tuning tasks.",
        related: ["Google DeepMind", "Multimodal AI", "Context Window", "LLM"],
      },
      {
        id: "llama3",
        term: "LLaMA 3",
        shortDef:
          "Meta's open-weight model family — frontier performance released publicly for research and commercial use.",
        definition:
          "LLaMA 3 (Meta, 2024) was a landmark open-weight release: LLaMA 3.1 405B matched GPT-4 on many benchmarks while being freely downloadable. Smaller variants (8B, 70B) offer near-GPT-3.5 quality on consumer hardware. LLaMA 3.2 added multimodal (vision) capabilities and on-device optimised 1B/3B models. The open release strategy rapidly accelerated the open-source AI ecosystem — Llama 3 became the base for hundreds of fine-tuned variants (Dolphin, Hermes, Llama Guard) on Hugging Face. LLaMA 3.3 70B is competitive with closed 200B+ models.",
        related: [
          "Meta AI",
          "Open-Weight Models",
          "Fine-tuning",
          "LoRA",
          "Quantization",
        ],
      },
      {
        id: "mistral-mixtral",
        term: "Mistral / Mixtral",
        shortDef:
          "Mistral AI's efficient open models — Mistral 7B punches above its weight; Mixtral uses sparse MoE.",
        definition:
          "Mistral 7B (2023) shocked the community by outperforming LLaMA 2 13B on most benchmarks despite being smaller, using grouped query attention and sliding window attention for efficiency. Mixtral 8x7B (2024) extends this with a Mixture-of-Experts architecture: 8 expert FFN layers, routing each token to 2, giving 46B total params but only 12.9B active per token — matching GPT-3.5 quality at far lower inference cost. Mistral Large 2 (2024) competes with GPT-4. All models are released under permissive Apache 2.0 licences, making them popular for self-hosted deployment.",
        related: ["Mistral AI", "Mixture of Experts", "Quantization", "LLM"],
      },
      {
        id: "deepseek-r1",
        term: "DeepSeek-R1",
        shortDef:
          "Open-weight reasoning model trained with pure RL — matches o1 at a fraction of the cost.",
        definition:
          "DeepSeek-R1 (DeepSeek, January 2025) is an open-weight reasoning model trained using GRPO — a pure reinforcement learning approach with no human-labelled reasoning chains. It matched OpenAI o1 on math (AIME), science (GPQA), and coding (Codeforces) benchmarks while being fully open-weight and orders of magnitude cheaper to run via API. This release demonstrated that reasoning capabilities can emerge from RL on verifiable rewards alone, without distillation from o1. DeepSeek also released R1-Distill variants (1.5B to 70B) that distil R1's reasoning into smaller dense models.",
        related: ["DeepSeek", "Reasoning Models", "GRPO", "Open-Weight Models"],
      },
      {
        id: "grok",
        term: "Grok",
        shortDef:
          "xAI's LLM — capable, long-context, with real-time X (Twitter) data access.",
        definition:
          "Grok is xAI's flagship LLM, named after the Heinlein term for deep intuitive understanding. Grok 1 (released open-weight in 2024) was a 314B MoE model. Grok 2 added web search, image generation, and reasoning capabilities. Grok 3 (2025) features DeepThink mode for extended chain-of-thought reasoning and reportedly scored near the top of frontier model benchmarks. Grok is unique in having real-time access to X (Twitter) data, making it strong for current-events queries. It is integrated into X Premium subscriptions.",
        related: ["xAI", "Reasoning Models", "Mixture of Experts", "LLM"],
      },
      {
        id: "qwen",
        term: "Qwen",
        shortDef:
          "Alibaba's open-weight model series — strong multilingual coverage and coding ability.",
        definition:
          "Qwen (Alibaba Cloud, 2023–) is a multilingual model family with particularly strong Chinese-English performance. Qwen 2.5 (2024) released models from 0.5B to 72B, with 72B competing with LLaMA 3.1 70B. Qwen 2.5-Coder and Qwen 2.5-Math are specialised variants. QwQ-32B is Qwen's reasoning model, matching DeepSeek-R1 on many benchmarks. Qwen models are used extensively in Asia and as base models for multilingual fine-tunes. Qwen-VL adds vision understanding. All flagship models are available on Hugging Face under relatively permissive licences.",
        related: [
          "Open-Weight Models",
          "Reasoning Models",
          "Multilingual LLM",
          "LLM",
        ],
      },
    ],
    "models",
  ),

  // ── AI Labs & Tools ─────────────────────────────────────────
  ...withCategory(
    [
      {
        id: "openai",
        term: "OpenAI",
        shortDef:
          "AI lab behind ChatGPT, GPT-4, and the o-series reasoning models — the company that triggered the LLM era.",
        definition:
          "Founded in 2015 as a non-profit (now a capped-profit company), OpenAI launched the modern LLM era with GPT-3 (2020) and made it mainstream with ChatGPT (November 2022). Key products: ChatGPT (consumer chat), GPT-4o (flagship API model), o1/o3 (reasoning models), Sora (text-to-video), DALL-E (text-to-image), Whisper (speech-to-text), and Codex (code generation, basis of GitHub Copilot). OpenAI also pioneered RLHF, function calling, and the Assistants API. It has a strategic partnership and major investment from Microsoft, powering Azure OpenAI and Microsoft Copilot.",
        related: [
          "GPT-4o",
          "Reasoning Models",
          "RLHF",
          "ChatGPT",
          "Function Calling",
        ],
      },
      {
        id: "anthropic",
        term: "Anthropic",
        shortDef:
          "Safety-focused AI lab founded by ex-OpenAI team — builders of Claude and Constitutional AI.",
        definition:
          "Anthropic was founded in 2021 by Dario Amodei, Daniela Amodei, and others who left OpenAI. Its primary product is the Claude model family, known for safety, long context, and instruction-following quality. Anthropic introduced Constitutional AI as its alignment methodology and pioneered mechanistic interpretability research through its interpretability team. It also created the Model Context Protocol (MCP), which became a de-facto standard for connecting LLMs to tools. Major investors include Google and Amazon (multi-billion dollar commitments). Anthropic publishes safety research alongside model releases.",
        related: [
          "Claude",
          "Constitutional AI",
          "MCP",
          "Mechanistic Interpretability",
        ],
      },
      {
        id: "google-deepmind",
        term: "Google DeepMind",
        shortDef:
          "Google's merged AI research division — Gemini, AlphaFold, and foundational deep learning research.",
        definition:
          "Google DeepMind was formed in 2023 by merging Google Brain (creators of TensorFlow, Transformer, BERT) and DeepMind (creators of AlphaGo, AlphaFold, AlphaCode). It now develops the Gemini model family (Google's flagship LLM), Gemma (open-weight), Veo (video generation), Imagen (image generation), and AlphaFold 3 (protein structure prediction). Google DeepMind also pioneered deep RL (DQN, AlphaZero), published Chinchilla scaling laws, and invented the Transformer architecture. It powers Google Search, Google Workspace AI, and Android AI features.",
        related: [
          "Gemini",
          "Transformer Architecture",
          "Scaling Laws",
          "Reinforcement Learning",
        ],
      },
      {
        id: "meta-ai",
        term: "Meta AI",
        shortDef:
          "Meta's AI research division — champions open-source AI through the LLaMA model family.",
        definition:
          "Meta AI (formerly FAIR — Facebook AI Research) has positioned itself as the leading open-source frontier lab. LLaMA 1, 2, and 3 are widely used as base models across the community. Meta also produced Llama Guard (safety classifier), Code Llama, Segment Anything Model (SAM for image segmentation), ImageBind (multimodal embeddings), and Emu (image generation). Meta integrates AI into WhatsApp, Instagram, Facebook, and its Ray-Ban smart glasses. Meta's open-weight strategy has been praised for democratising AI access but also criticised for potential misuse risks.",
        related: ["LLaMA 3", "Open-Weight Models", "LLM", "Multimodal AI"],
      },
      {
        id: "mistral-ai",
        term: "Mistral AI",
        shortDef:
          "French AI startup building efficient, open-weight frontier models with Apache 2.0 licences.",
        definition:
          "Mistral AI was founded in Paris in 2023 by former DeepMind and Meta researchers. It rapidly gained credibility by releasing Mistral 7B (outperforming larger models), Mixtral 8x7B (efficient MoE), and Mistral Large (frontier-class). Its commercial models are available via La Plateforme API. Mistral is notable for its strong open-weight releases under permissive licences, European funding (backed by Andreessen Horowitz, Lightspeed, BNP Paribas), and GDPR-compliant data residency options. It is a major player in enterprise AI deployments that require on-premise or European-hosted solutions.",
        related: [
          "Mistral / Mixtral",
          "Open-Weight Models",
          "Mixture of Experts",
          "LoRA",
        ],
      },
      {
        id: "hugging-face",
        term: "Hugging Face",
        shortDef:
          "The GitHub of AI — central hub for open-source models, datasets, and ML tools.",
        definition:
          "Hugging Face hosts the world's largest repository of open-source ML models (600K+), datasets (150K+), and Spaces (hosted ML demos). Its `transformers` library is the de-facto standard for loading and running pretrained models in PyTorch and JAX. The `datasets`, `accelerate`, `peft` (LoRA fine-tuning), `diffusers` (Stable Diffusion), and `trl` (RLHF training) libraries together form a complete open-source ML stack. Hugging Face also publishes the Open LLM Leaderboard for benchmarking open models and runs the BLOOM/BLOOMZ multilingual model collaborations. It is essential infrastructure for the open AI ecosystem.",
        related: [
          "LoRA",
          "Fine-tuning",
          "Open-Weight Models",
          "LLaMA 3",
          "Diffusion Model",
        ],
      },
      {
        id: "xai",
        term: "xAI",
        shortDef:
          "Elon Musk's AI company — builders of Grok, with a mission to understand the universe.",
        definition:
          "xAI was founded by Elon Musk in 2023 after he departed OpenAI's board. Its flagship product is Grok, an LLM integrated into X (Twitter) with real-time X data access. xAI open-sourced Grok 1 (314B MoE parameters) in March 2024, making it the largest openly released model at the time. Grok 3 competed at the frontier level with extended reasoning (DeepThink mode). xAI raised $6B+ in its Series B and is building a 100K+ GPU data center (Colossus) in Memphis. The company's stated mission is 'to understand the true nature of the universe'.",
        related: ["Grok", "LLM", "Mixture of Experts", "Reasoning Models"],
      },
      {
        id: "perplexity-ai",
        term: "Perplexity AI",
        shortDef:
          "AI-powered search engine — combines real-time web retrieval with LLM synthesis to answer queries.",
        definition:
          "Perplexity AI (founded 2022) reimagines web search: instead of a list of links, it retrieves real-time web results and synthesises them into a direct, cited answer using an LLM. This makes it closer to a research assistant than a traditional search engine. It uses a blend of its own and third-party models (GPT-4, Claude) behind its interface. Key differentiators: inline citations for every claim, follow-up question suggestions, and a Pro tier with advanced model access. Perplexity reached 15M monthly active users in 2024 and is frequently cited as a genuine threat to Google Search.",
        related: ["RAG", "Grounding", "Semantic Search", "LLM"],
      },
      {
        id: "cursor",
        term: "Cursor",
        shortDef:
          "AI-first code editor — VS Code fork with deep LLM integration for coding, refactoring, and chat.",
        definition:
          "Cursor is an IDE built on VS Code that integrates frontier LLMs (GPT-4o, Claude 3.5) directly into the editing experience. Key features: Tab (AI autocomplete that predicts multi-line edits), Chat (natural language codebase Q&A with full file context), Composer (agent that autonomously edits multiple files), and MCP support for external tool connections. Cursor popularised 'vibe coding' workflows and attracted engineers moving away from GitHub Copilot for its more native agentic experience. It reached $100M ARR in 2024. The underlying Agent mode uses Claude's extended thinking for complex refactors.",
        related: ["Vibe Coding", "MCP", "AI Agent", "LLM", "Function Calling"],
      },
      {
        id: "deepseek",
        term: "DeepSeek",
        shortDef:
          "Chinese AI lab that stunned the world with frontier open-weight models at exceptionally low cost.",
        definition:
          "DeepSeek is a Chinese AI research lab (subsidiary of the quantitative hedge fund High-Flyer) that released a series of frontier models at fraction of expected cost. DeepSeek-V2 (2024) introduced Multi-head Latent Attention (MLA) to radically reduce KV cache memory. DeepSeek-V3 (December 2024) was trained for ~$5.5M — orders of magnitude cheaper than comparable models — using FP8 mixed precision, pipeline optimisations, and GRPO. DeepSeek-R1 (January 2025) matched OpenAI o1 on reasoning benchmarks as a fully open-weight model. The releases triggered significant re-evaluation of AI hardware investment theses and demonstrated that efficiency innovation can rapidly close the gap with brute-force scaling.",
        related: ["DeepSeek-R1", "GRPO", "Open-Weight Models", "Quantization"],
      },
    ],
    "labs",
  ),
];
